/**
 * pages-today.ts — หน้า "กำไรวันนี้" (Simple Mode)
 * โฟกัสสิ่งที่แม่ค้าอยากเห็น: กำไรจริง + อะไรกินกำไร + ยอดขายแยกช่องทาง
 * ใช้ข้อมูลชุดเดียวกับ Dashboard/รายงาน (getView) — ไม่มี engine ใหม่
 */
import type { PnlLineKey } from '@rrp/shared';
import type { Page } from './router.js';
import { formatTHB, fmtPct } from './format.js';
import { getView } from './view.js';
import { setFilters, presetRange } from './filters.js';
import { amountOf, pctOf, bannerHtml, channelMixHtml } from './pages.js';
import { getTenant } from './state.js';
import { listOrders, syncStatus } from './api.js';
import {
  buildTaskQueue,
  buildSetupChecklist,
  allStepsDone,
  taskQueueHtml,
  checklistHtml,
  type HomeSignals,
} from './home-tasks.js';

/** สัญญาณพื้นฐานจาก view (ใช้ได้ทั้ง demo/live) */
function baseSignals(): HomeSignals {
  const v = getView();
  const sales = amountOf(v.lines, 'sales');
  const totalSga = amountOf(v.lines, 'total_sga');
  const hasSales = sales !== 0;
  return {
    missingCostCount: v.completeness.missingCostCount,
    affectedRevenue: formatTHB(v.completeness.affectedRevenue),
    hasSales,
    hasOrders: hasSales,
    hasExpense: totalSga > 0,
  };
}

/** แปลงเวลาซิงค์ล่าสุดจาก sync jobs → "HH:mm น." (best-effort) */
function lastSyncLabel(jobs: Record<string, unknown>[]): string | null {
  let latest = 0;
  for (const j of jobs) {
    for (const k of ['finished_at', 'updated_at', 'created_at']) {
      const raw = j[k];
      if (typeof raw === 'string') {
        const t = Date.parse(raw);
        if (Number.isFinite(t) && t > latest) latest = t;
      }
    }
  }
  if (!latest) return null;
  try {
    return (
      new Date(latest).toLocaleTimeString('th-TH', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Bangkok',
      }) + ' น.'
    );
  } catch {
    return null;
  }
}

/** หมวดที่ "กินกำไร" — เรียงจากมากไปน้อยตามยอดจริง */
const EAT_KEYS: { key: PnlLineKey; label: string }[] = [
  { key: 'cost_of_products', label: 'ต้นทุนสินค้า' },
  { key: 'commissions_platform_fees', label: 'ค่าคอม + ค่าแพลตฟอร์ม' },
  { key: 'transportation_fulfillment', label: 'ค่าส่ง + ค่ากล่อง' },
  { key: 'marketing_expenses', label: 'ค่าแอด / การตลาด' },
  { key: 'total_sga', label: 'ค่าใช้จ่ายบริหาร' },
];

function eatProfitHtml(): string {
  const lines = getView().lines;
  const items = EAT_KEYS.map((e) => ({ label: e.label, amount: amountOf(lines, e.key) })).filter(
    (i) => i.amount > 0,
  );
  items.sort((a, b) => b.amount - a.amount);
  if (items.length === 0) return '<p class="muted">ยังไม่มีรายจ่ายในช่วงนี้</p>';
  const max = Math.max(...items.map((i) => i.amount), 1);
  return items
    .map((i) => {
      const w = (i.amount / max) * 100;
      return `<div class="eat-bar">
        <div class="eat-lbl"><span>${i.label}</span><span class="num">−${formatTHB(i.amount).replace('฿', '')}</span></div>
        <div class="eat-track"><div class="eat-fill" style="width:${w.toFixed(1)}%"></div></div>
      </div>`;
    })
    .join('');
}

/** งบกำไรขาดทุนแบบย่อ 8 บรรทัด (Simple Mode) — ดึงจาก 26 บรรทัดเดิม ไม่มีสูตรใหม่ */
// โหมดง่าย: ใช้คำที่แม่ค้าเข้าใจ (เลี่ยงศัพท์บัญชี EBITDA/GP/NP)
const CONDENSED: {
  key: PnlLineKey;
  label: string;
  pct?: PnlLineKey;
  strong?: boolean;
  tip?: string;
}[] = [
  { key: 'sales', label: 'ยอดขาย', tip: 'เงินที่ขายของได้ทั้งหมด' },
  { key: 'total_income', label: 'รายได้รวม', tip: 'ยอดขาย + รายได้อื่น ๆ' },
  {
    key: 'total_costs_of_sale',
    label: 'ต้นทุนสินค้า + ค่าส่ง/แอด',
    tip: 'ต้นทุนของที่ขาย + ค่าส่ง + ค่าโฆษณา รวมกัน',
  },
  { key: 'gross_margin', label: 'กำไรหลังหักต้นทุนขาย', pct: 'gp_pct', tip: 'เหลือเท่าไรหลังหักต้นทุนของที่ขาย' },
  {
    key: 'total_sga',
    label: 'ค่าใช้จ่ายดำเนินงาน (เงินเดือน/เช่า/ฯลฯ)',
    tip: 'ค่าใช้จ่ายประจำที่ไม่ใช่ต้นทุนของ',
  },
  { key: 'ebitda', label: 'กำไรจากการดำเนินงาน', tip: 'กำไรจากการขายจริง ก่อนหักภาษี' },
  { key: 'corporate_income_tax', label: 'ภาษีเงินได้', tip: 'ภาษีนิติบุคคล (ใส่ยอดจริงจากบัญชี)' },
  {
    key: 'net_profit',
    label: 'กำไรสุทธิ (เหลือจริง)',
    pct: 'np_pct',
    strong: true,
    tip: 'เงินที่เหลือเข้ากระเป๋าจริง หลังหักทุกอย่าง',
  },
];

export function condensedPnlHtml(): string {
  const v = getView();
  const rows = CONDENSED.map((c) => {
    const amt = amountOf(v.lines, c.key);
    const numCls = amt < 0 ? 'num neg-num' : 'num';
    const pctCell = c.pct ? ` <span class="muted">(${fmtPct(pctOf(v.lines, c.pct))})</span>` : '';
    const tip = c.tip ? `<div class="line-tip">${c.tip}</div>` : '';
    return `<tr class="${c.strong ? 'total' : ''}"><td>${c.label}${pctCell}${tip}</td><td class="${numCls}">${formatTHB(amt)}</td></tr>`;
  }).join('');
  return `<div class="table-scroll"><table class="pnl-table">${rows}</table></div>
    <p class="foot-note">ดูแบบเต็ม 26 บรรทัด + เจาะรายการที่ <a href="#pnl">รายงานกำไรขาดทุน →</a></p>`;
}

// ── เฟส 3c: welcome ครั้งแรกสำหรับบัญชีใหม่ ──
function welcomeSeen(): boolean {
  try {
    return localStorage.getItem('rrp_onboarded') === '1';
  } catch {
    return true;
  }
}
function markWelcomeSeen(): void {
  try {
    localStorage.setItem('rrp_onboarded', '1');
  } catch {
    /* ignore */
  }
}
function showWelcome(): void {
  const back = document.createElement('div');
  back.className = 'modal-back';
  back.innerHTML = `<div class="modal" role="dialog" aria-modal="true" aria-label="ยินดีต้อนรับ">
    <h3 class="modal-title">ยินดีต้อนรับสู่ RRP P&amp;L 👋</h3>
    <p class="modal-msg">เริ่มใช้งานง่าย ๆ 3 ขั้น แล้วเห็นกำไรจริงได้เลย</p>
    <div class="welcome-steps">
      <div class="welcome-step"><span class="wn">1</span><span class="wl">เชื่อมร้าน TikTok หรือนำเข้ายอดขาย</span></div>
      <div class="welcome-step"><span class="wn">2</span><span class="wl">ใส่ต้นทุนสินค้า ให้กำไรครบ</span></div>
      <div class="welcome-step"><span class="wn">3</span><span class="wl">บันทึกค่าใช้จ่ายก้อนแรก (เลือกโค้ด)</span></div>
    </div>
    <div class="modal-actions"><button type="button" class="modal-ok" id="welcome-go">เริ่มเลย</button></div>
  </div>`;
  const close = (): void => {
    markWelcomeSeen();
    back.remove();
  };
  back.querySelector('#welcome-go')!.addEventListener('click', close);
  back.addEventListener('click', (e) => {
    if (e.target === back) close();
  });
  document.body.appendChild(back);
}

/** แถบเลือกช่วงแบบง่าย (วันนี้ / 7 วัน / เดือนนี้) */
function rangeChips(): string {
  const presets: [string, string][] = [
    ['today', 'วันนี้'],
    ['last7', '7 วัน'],
    ['this_month', 'เดือนนี้'],
  ];
  return `<div class="range-chips">${presets
    .map(([k, l]) => `<button type="button" class="chip-range" data-preset="${k}">${l}</button>`)
    .join('')}</div>`;
}

export const profitTodayPage: Page = {
  title: 'กำไรวันนี้',
  render() {
    const v = getView();
    const profit = amountOf(v.lines, 'net_profit');
    const prevProfit = v.prevAmounts['net_profit'] ?? 0;
    const income = amountOf(v.lines, 'total_income');
    const expense = income - profit;
    const npPct = pctOf(v.lines, 'np_pct');
    const delta = profit - prevProfit;
    const deltaPct = prevProfit !== 0 ? (delta / Math.abs(prevProfit)) * 100 : null;
    const pos = profit >= 0;
    const deltaPos = delta >= 0;

    const signals = baseSignals();
    const queue = buildTaskQueue(signals);
    const setup = buildSetupChecklist(signals);

    const head = `
      <div class="topbar"><h1>กำไรวันนี้</h1>${rangeChips()}</div>
      <div class="quick-add-row"><a class="btn-quick-add" href="#add">＋ บันทึกรายรับ-รายจ่าย</a></div>
      ${bannerHtml()}`;

    // ยังไม่มีข้อมูลในช่วงนี้ → หน้าต้อนรับ + ขั้นเริ่มต้น (แทนตัวเลข ฿0 ที่ว่างเปล่า)
    if (income === 0 && expense === 0) {
      return `${head}
        <div class="card empty-hero">
          <div class="empty-emoji">📊</div>
          <h2>ยังไม่มีตัวเลขในช่วงนี้</h2>
          <p class="empty-sub">บันทึกข้อมูลร้านสัก 2–3 อย่าง แล้วกำไรขาดทุนจะขึ้นให้อัตโนมัติ</p>
          <a class="btn empty-cta" href="#add">＋ เริ่มบันทึกรายรับ-รายจ่าย</a>
        </div>
        ${checklistHtml(setup)}
        <div id="home-queue-slot">${taskQueueHtml(queue)}</div>`;
    }

    return `${head}
      <div class="card today-hero ${pos ? 'good' : 'bad'}">
        <div class="today-hero-head">กำไรจริง <span class="muted">// ยอดขาย − ทุกอย่างที่หัก</span></div>
        <div class="today-big num ${pos ? 'pos' : 'neg'}">${pos ? '+' : ''}${formatTHB(profit).replace('฿', '')}</div>
        <div class="today-sub">
          <span class="delta-chip ${deltaPos ? 'up' : 'down'}">${deltaPos ? '▲' : '▼'} ${
            deltaPct === null ? formatTHB(delta) : `${deltaPos ? '+' : ''}${deltaPct.toFixed(0)}%`
          } <span class="muted">จากงวดก่อน</span></span>
        </div>
        <div class="today-mini">
          <div class="m"><div class="l">ยอดขาย</div><div class="v num">${formatTHB(income)}</div></div>
          <div class="m"><div class="l">รายจ่ายรวม</div><div class="v num">${formatTHB(expense)}</div></div>
          <div class="m"><div class="l">กำไร %</div><div class="v ${pos ? 'pos' : 'neg'}">${fmtPct(npPct)}</div></div>
        </div>
      </div>

      ${allStepsDone(setup) ? '' : checklistHtml(setup)}
      <div id="home-queue-slot">${taskQueueHtml(queue)}</div>

      <div class="today-grid">
        <div class="card"><h3>อะไรกินกำไรกรู <span class="muted">// เรียงมาก → น้อย</span></h3>${eatProfitHtml()}</div>
        <div class="card"><h3>ยอดขายแยกช่องทาง</h3>${channelMixHtml()}
          <p class="foot-note">ดู SKU ตัวทำเงิน/ตัวขาดทุนรายตัวที่ <a href="#skuprofit">ตัวทำเงิน / ตัวขาดทุน →</a></p>
        </div>
      </div>

      <div class="card"><h3>งบกำไรขาดทุนแบบย่อ <span class="muted">// 8 บรรทัด</span></h3>${condensedPnlHtml()}</div>`;
  },
  mount(root) {
    root.querySelectorAll<HTMLButtonElement>('.chip-range[data-preset]').forEach((b) =>
      b.addEventListener('click', () => setFilters(presetRange(b.dataset['preset']!))),
    );

    // live: เติมคิวด้วย "ออเดอร์รอเงิน" + "สถานะซิงค์" จาก API (best-effort)
    if (getTenant()) {
      // เฟส 3c: บัญชีใหม่ (ยังไม่มีข้อมูล) เห็น welcome ครั้งเดียว
      const s0 = baseSignals();
      if (!s0.hasSales && !s0.hasExpense && !welcomeSeen()) showWelcome();

      const slot = root.querySelector<HTMLElement>('#home-queue-slot');
      if (!slot) return;
      void Promise.allSettled([listOrders({ unpaidOnly: true, limit: 1 }), syncStatus()]).then(
        ([ordersR, syncR]) => {
          const signals = baseSignals();
          if (ordersR.status === 'fulfilled') {
            const r = ordersR.value;
            signals.unpaidOrderCount = r.total ?? r.items.length;
            if ((signals.unpaidOrderCount ?? 0) > 0) signals.hasOrders = true;
          }
          if (syncR.status === 'fulfilled') {
            signals.lastSyncLabel = lastSyncLabel(
              (syncR.value.jobs ?? []) as Record<string, unknown>[],
            );
          }
          slot.innerHTML = taskQueueHtml(buildTaskQueue(signals));
        },
      );
    }
  },
};
